import {
  AmountListItem,
  Button,
  Chip,
  ErrorAlert,
  Icon,
  InfoAlert,
  Loading,
  LoadingAlert,
  Section,
  Table,
  TokenListItem,
  Tooltip,
  WalletTokenListItem,
} from '@/components';
import { useNavigation } from '@/components/providers';
import { useTradeableTokens } from '@/components/providers/postauth/TradeableTokensProvider';
import { useWalletsContext } from '@/components/providers/postauth/UserWalletsProvider';
import {
  ErrorCode,
  type ErrorInterface,
  TokenInterface,
  WalletTokenInterface,
} from '@/interfaces';
import { replaceTokensInString } from '@/utils/functionalUtils';
import { logError, logInfo } from '@/utils/logger';
import { TRADE, TRADE_CARD } from '@/utils/strings';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { TradeFormDataInterface } from './interfaces/TradeInterfaces';
import { SCREENS } from './TradeCard';
const visualizeElementsAndSpacing = false;
const borderClassToVisualizeElementsAndSpacing = visualizeElementsAndSpacing
  ? 'border-white border'
  : '';
const spacingConfig = {
  rootGap: 'gap-sm',
  gapBetweenTableCellAndTooltip: 'gap-sm',
  gapBetweenCancelAndConfirmButtons: 'gap-sm',
};
interface Props {
  tradeData: TradeFormDataInterface;
  handleSuccessfulTradeConfirmation: () => void;
}
const ERROR_MESSAGES = {
  [ErrorCode.INSUFFICIENT_BALANCE]: {
    title:
      TRADE_CARD.SCREENS.TRADE_CONFIRMATION_REQUEST.MESSAGES.ERRORS
        .FEE_ESTIMATE_FETCH_INSUFFICIENT_BALANCE_ERROR_TITLE,
    description:
      TRADE_CARD.SCREENS.TRADE_CONFIRMATION_REQUEST.MESSAGES.ERRORS
        .FEE_ESTIMATE_FETCH_INSUFFICIENT_BALANCE_ERROR_DESCRIPTION,
  },
  [ErrorCode.UNSUPPORTED_TOKEN]: {
    title:
      TRADE_CARD.SCREENS.TRADE_CONFIRMATION_REQUEST.MESSAGES.ERRORS
        .FEE_ESTIMATE_FETCH_ASSET_NOT_SUPPORTED_ERROR_TITLE,
    description:
      TRADE_CARD.SCREENS.TRADE_CONFIRMATION_REQUEST.MESSAGES.ERRORS
        .FEE_ESTIMATE_FETCH_ASSET_NOT_SUPPORTED_ERROR_DESCRIPTION,
  },
  [ErrorCode.INSUFFICIENT_AMOUNT]: {
    title:
      TRADE_CARD.SCREENS.TRADE_CONFIRMATION_REQUEST.MESSAGES.ERRORS
        .FEE_ESTIMATE_FETCH_INSUFFICIENT_AMOUNT_ERROR_TITLE,
    description:
      TRADE_CARD.SCREENS.TRADE_CONFIRMATION_REQUEST.MESSAGES.ERRORS
        .FEE_ESTIMATE_FETCH_INSUFFICIENT_AMOUNT_ERROR_DESCRIPTION,
  },
  [ErrorCode.SERVER_ERROR]: {
    title:
      TRADE_CARD.SCREENS.TRADE_CONFIRMATION_REQUEST.MESSAGES.ERRORS
        .FEE_ESTIMATE_FETCH_GENERIC_ERROR_TITLE,
    description:
      TRADE_CARD.SCREENS.TRADE_CONFIRMATION_REQUEST.MESSAGES.ERRORS
        .FEE_ESTIMATE_FETCH_GENERIC_ERROR_DESCRIPTION,
  },
  [ErrorCode.SWAP_ROUTE_NOT_FOUND]: {
    title:
      TRADE_CARD.SCREENS.TRADE_CONFIRMATION_REQUEST.MESSAGES.ERRORS
        .FEE_ESTIMATE_FETCH_ASSET_NOT_SUPPORTED_ERROR_TITLE,
    description:
      TRADE_CARD.SCREENS.TRADE_CONFIRMATION_REQUEST.MESSAGES.ERRORS
        .FEE_ESTIMATE_FETCH_ASSET_NOT_SUPPORTED_ERROR_DESCRIPTION,
  },
} as const;
const TradeConfirmationRequest: FC<Props> = ({
  tradeData,
  handleSuccessfulTradeConfirmation: confirmTrade,
}) => {
  const { navigateBack, disableBackNavigation } = useNavigation();
  const { sellAsset, buyAsset, amount } = tradeData;
  const { refreshWalletsAndTransactionsWithBackoff } = useWalletsContext();
  const { getTradeFee, trade } = useTradeableTokens();
  const [fees, setFees] = useState<{
    convenienceFee: string;
    convenienceFeeInUsd: string;
    gasCost: string;
    gasCostInUsd: string;
    bridgeFee: string;
    bridgeFeeInUsd: string;
    totalFee: string;
    totalFeeInUsd: string;
    amountOut: string;
    amountOutMin: string;
  } | null>(null);
  const [isFetchingFees, setIsFetchingFees] = useState(false);
  const [feeFetchError, setFeeFetchError] = useState<ErrorInterface | null>(
    null,
  );
  const [isProcessing, setIsProcessing] = useState(false);
  const [tradeError, setTradeError] = useState<ErrorInterface | null>(null);
  const [showFeeEstimationLoadingAlert, setShowFeeEstimationLoadingAlert] =
    useState(false);
  const [showTradeLoadingAlert, setShowTradeLoadingAlert] = useState(false);
  const amountInUsd = useMemo(
    () => amount * (sellAsset?.priceInUsd || 0),
    [amount, sellAsset?.priceInUsd],
  );
  const replacements = useMemo(
    () => ({
      sell_asset_name: sellAsset?.name || '',
      sell_asset_symbol: sellAsset?.symbol || '',
      sell_asset_network: `Network ` + sellAsset?.blockchainDisplayName || '',
      buy_asset_name: buyAsset?.name || '',
      buy_asset_symbol: buyAsset?.symbol || '',
      buy_asset_network: `Network ` + buyAsset?.blockchainDisplayName || '',
      convenience_fee_percent:
        amount > 0 && fees
          ? ((parseFloat(fees.convenienceFee) / amount) * 100).toFixed(2)
          : '0.00',
    }),
    [sellAsset, buyAsset, amount, fees],
  );
  const { convenienceFee, convenienceFeeInUsd, networkFee, networkFeeInUsd } =
    useMemo(
      () => ({
        convenienceFee: fees ? parseFloat(fees.convenienceFee) : 0,
        convenienceFeeInUsd: fees ? parseFloat(fees.convenienceFeeInUsd) : 0,
        networkFee: fees
          ? parseFloat(fees.gasCost) + parseFloat(fees.bridgeFee || '0')
          : 0,
        networkFeeInUsd: fees
          ? parseFloat(fees.gasCostInUsd) +
            parseFloat(fees.bridgeFeeInUsd || '0')
          : 0,
      }),
      [fees],
    );
  useEffect(() => {
    let isMounted = true;
    let feeEstimationTimer: NodeJS.Timeout | null = null;
    const fetchFees = async () => {
      if (sellAsset && buyAsset && amount) {
        setIsFetchingFees(true);
        feeEstimationTimer = setTimeout(() => {
          if (isMounted) {
            setShowFeeEstimationLoadingAlert(true);
          }
        }, 5000);
        try {
          const result = await getTradeFee(
            { address: sellAsset.address, blockchain: sellAsset.blockchain },
            { address: buyAsset.address, blockchain: buyAsset.blockchain },
            amount.toString(),
            tradeData.yieldBearingNativeAssetId,
          );
          if (isMounted) setFees(result);
        } catch (error: any) {
          if (isMounted) {
            setFees(null);
            const errorDetails: ErrorInterface = error;
            logError(
              errorDetails.statusCode
                ? `Error fetching fees: ${errorDetails.errorMessage}`
                : 'Unexpected error fetching fees',
              'TradeConfirmationRequest.fetchTradeFees',
              errorDetails,
            );
            setFeeFetchError(errorDetails);
          }
        } finally {
          if (isMounted) {
            if (feeEstimationTimer) clearTimeout(feeEstimationTimer);
            setIsFetchingFees(false);
            setShowFeeEstimationLoadingAlert(false);
          }
        }
      }
    };
    fetchFees();
    return () => {
      isMounted = false;
      if (feeEstimationTimer) clearTimeout(feeEstimationTimer);
    };
  }, [
    sellAsset,
    buyAsset,
    amount,
    getTradeFee,
    tradeData.yieldBearingNativeAssetId,
  ]);
  const handleConfirm = useCallback(async () => {
    if (!sellAsset || !buyAsset || amount <= 0) {
      logError('Invalid trade data', 'TradeConfirmationRequest.handleConfirm', {
        sellAsset,
        buyAsset,
        amount,
      });
      return;
    }
    setIsProcessing(true);
    setTradeError(null);
    disableBackNavigation();
    const tradeTimer = setTimeout(() => {
      setShowTradeLoadingAlert(true);
    }, 5000);
    try {
      const response = await trade(
        { address: sellAsset.address, blockchain: sellAsset.blockchain },
        { address: buyAsset.address, blockchain: buyAsset.blockchain },
        amount.toString(),
        tradeData.yieldBearingNativeAssetId,
      );
      logInfo(
        'Trade successful',
        'TradeConfirmationRequest.handleConfirm',
        response,
      );
      tradeData.response = response;
      confirmTrade();
      refreshWalletsAndTransactionsWithBackoff();
    } catch (error: any) {
      const errorDetails: ErrorInterface = error;
      logError(errorDetails, 'TradeConfirmationRequest.handleConfirm', {
        sellAsset,
        buyAsset,
        amount,
      });
      setTradeError(errorDetails);
    } finally {
      clearTimeout(tradeTimer);
      setIsProcessing(false);
      setShowTradeLoadingAlert(false);
    }
  }, [
    sellAsset,
    buyAsset,
    amount,
    trade,
    confirmTrade,
    refreshWalletsAndTransactionsWithBackoff,
    disableBackNavigation,
    tradeData,
  ]);
  const InfoTooltip: FC<{ titleKey: string; descriptionKey: string }> =
    useCallback(
      ({ titleKey, descriptionKey }) => (
        <Tooltip
          trigger={<Icon name="question" intent="accent" />}
          align="right"
          isFullBleed
        >
          <InfoAlert
            title={replaceTokensInString(titleKey, replacements)}
            description={replaceTokensInString(descriptionKey, replacements)}
            intent="transparent"
          />
        </Tooltip>
      ),
      [replacements],
    );
  const rows = useMemo(
    () => [
      {
        cells: [
          { mainText: 'Buy' },
          {
            mainText: (
              <div className="flex items-center gap-sm">
                <TokenListItem
                  token={buyAsset as WalletTokenInterface}
                  align="right"
                  showTicker
                  showNetwork
                  showNetworkPrefix
                />
                <InfoTooltip
                  titleKey={
                    TRADE_CARD.SCREENS.TRADE_CONFIRMATION_REQUEST.MESSAGES
                      .TOOLTIPS.BUY_ASSET_TITLE
                  }
                  descriptionKey={
                    TRADE_CARD.SCREENS.TRADE_CONFIRMATION_REQUEST.MESSAGES
                      .TOOLTIPS.BUY_ASSET_DESCRIPTION
                  }
                />
              </div>
            ),
            align: 'right' as 'right',
          },
        ],
      },
      {
        cells: [
          { mainText: 'Sell' },
          {
            mainText: (
              <div className="flex items-center gap-sm">
                <WalletTokenListItem
                  token={sellAsset as WalletTokenInterface}
                  align="right"
                  showTicker
                  showBalance={false}
                  showWalletAddress={false}
                  showNetwork
                  showNetworkPrefix
                  walletBlockchain={
                    (sellAsset as WalletTokenInterface)?.walletBlockchain
                  }
                />
                <InfoTooltip
                  titleKey={
                    TRADE_CARD.SCREENS.TRADE_CONFIRMATION_REQUEST.MESSAGES
                      .TOOLTIPS.SELL_ASSET_TITLE
                  }
                  descriptionKey={
                    TRADE_CARD.SCREENS.TRADE_CONFIRMATION_REQUEST.MESSAGES
                      .TOOLTIPS.SELL_ASSET_DESCRIPTION
                  }
                />
              </div>
            ),
            align: 'right' as 'right',
          },
        ],
      },
      {
        cells: [
          { mainText: 'Amount' },
          {
            mainText: (
              <div className="flex items-center gap-sm">
                <AmountListItem
                  token={sellAsset as TokenInterface}
                  amount={amount}
                  subAmount={amountInUsd}
                />
                <InfoTooltip
                  titleKey={
                    TRADE_CARD.SCREENS.TRADE_CONFIRMATION_REQUEST.MESSAGES
                      .TOOLTIPS.AMOUNT_TITLE
                  }
                  descriptionKey={
                    TRADE_CARD.SCREENS.TRADE_CONFIRMATION_REQUEST.MESSAGES
                      .TOOLTIPS.AMOUNT_DESCRIPTION
                  }
                />
              </div>
            ),
            align: 'right' as 'right',
          },
        ],
      },
      {
        cells: [
          { mainText: 'Network Fee' },
          {
            mainText: (
              <div className="flex items-center gap-sm">
                {isFetchingFees ? (
                  <Loading size="xs" />
                ) : fees ? (
                  <AmountListItem
                    token={sellAsset as TokenInterface}
                    amount={networkFee}
                    subAmount={networkFeeInUsd}
                  />
                ) : (
                  <Chip
                    text={
                      TRADE_CARD.SCREENS.TRADE_CONFIRMATION_REQUEST.MESSAGES
                        .ERRORS
                        .FEE_ESTIMATE_FETCH_ASSET_NOT_SUPPORTED_ERROR_NETWORK_FEE_PLACEHOLDER
                    }
                    intent="error"
                    setChipToNoMargin={true}
                  />
                )}
                <InfoTooltip
                  titleKey={
                    TRADE_CARD.SCREENS.TRADE_CONFIRMATION_REQUEST.MESSAGES
                      .TOOLTIPS.NETWORK_FEE_TITLE
                  }
                  descriptionKey={
                    TRADE_CARD.SCREENS.TRADE_CONFIRMATION_REQUEST.MESSAGES
                      .TOOLTIPS.NETWORK_FEE_DESCRIPTION
                  }
                />
              </div>
            ),
            align: 'right' as 'right',
          },
        ],
      },
      {
        cells: [
          { mainText: 'Service Fee' },
          {
            mainText: (
              <div className="flex items-center gap-sm">
                {isFetchingFees ? (
                  <Loading size="xs" />
                ) : fees ? (
                  convenienceFee === 0 ? (
                    <Chip
                      text={`${TRADE_CARD.SCREENS.TRADE_CONFIRMATION_REQUEST.MESSAGES.CHIP_LABEL_FOR_FREE}`}
                      intent="accent"
                      setChipToNoMargin={true}
                    />
                  ) : (
                    <AmountListItem
                      token={sellAsset as TokenInterface}
                      amount={convenienceFee}
                      subAmount={convenienceFeeInUsd}
                    />
                  )
                ) : (
                  <Chip
                    text={
                      TRADE_CARD.SCREENS.TRADE_CONFIRMATION_REQUEST.MESSAGES
                        .ERRORS
                        .FEE_ESTIMATE_FETCH_ASSET_NOT_SUPPORTED_ERROR_CONVENIENCE_FEE_PLACEHOLDER
                    }
                    intent="error"
                    setChipToNoMargin={true}
                  />
                )}
                <InfoTooltip
                  titleKey={
                    TRADE_CARD.SCREENS.TRADE_CONFIRMATION_REQUEST.MESSAGES
                      .TOOLTIPS.CONVENIENCE_FEE_TITLE
                  }
                  descriptionKey={
                    TRADE_CARD.SCREENS.TRADE_CONFIRMATION_REQUEST.MESSAGES
                      .TOOLTIPS.CONVENIENCE_FEE_DESCRIPTION
                  }
                />
              </div>
            ),
            align: 'right' as 'right',
          },
        ],
      },
    ],
    [
      buyAsset,
      sellAsset,
      amount,
      isFetchingFees,
      fees,
      networkFee,
      networkFeeInUsd,
      convenienceFee,
      convenienceFeeInUsd,
      InfoTooltip,
    ],
  );
  const getErrorContent = useCallback(
    (error: ErrorInterface) => {
      if (
        error.errorCode === ErrorCode.SERVER_ERROR &&
        error.details === 'Invalid tokens'
      ) {
        return {
          title: replaceTokensInString(
            TRADE_CARD.SCREENS.TRADE_CONFIRMATION_REQUEST.MESSAGES.ERRORS
              .FEE_ESTIMATE_FETCH_ASSET_NOT_SUPPORTED_ERROR_TITLE,
            replacements,
          ),
          description: replaceTokensInString(
            TRADE_CARD.SCREENS.TRADE_CONFIRMATION_REQUEST.MESSAGES.ERRORS
              .FEE_ESTIMATE_FETCH_ASSET_NOT_SUPPORTED_ERROR_DESCRIPTION,
            replacements,
          ),
        };
      }
      if (
        error.errorCode &&
        ERROR_MESSAGES[error.errorCode as keyof typeof ERROR_MESSAGES]
      ) {
        const errorConfig =
          ERROR_MESSAGES[error.errorCode as keyof typeof ERROR_MESSAGES];
        return {
          title: replaceTokensInString(errorConfig.title, replacements),
          description: replaceTokensInString(
            errorConfig.description,
            replacements,
          ),
        };
      }
      return {
        title: replaceTokensInString(
          TRADE_CARD.SCREENS.TRADE_CONFIRMATION_REQUEST.MESSAGES.ERRORS
            .FEE_ESTIMATE_FETCH_ASSET_NOT_SUPPORTED_ERROR_TITLE,
          replacements,
        ),
        description: replaceTokensInString(
          TRADE_CARD.SCREENS.TRADE_CONFIRMATION_REQUEST.MESSAGES.ERRORS
            .FEE_ESTIMATE_FETCH_ASSET_NOT_SUPPORTED_ERROR_DESCRIPTION,
          replacements,
        ),
      };
    },
    [replacements],
  );
  const renderBody = useCallback(
    () => (
      <Section
        body={
          <div
            className={`flex flex-col ${spacingConfig.rootGap} ${borderClassToVisualizeElementsAndSpacing}`}
          >
            <Table rows={rows} columnWidths={[50, 50]} />
          </div>
        }
        className={borderClassToVisualizeElementsAndSpacing}
      />
    ),
    [rows, feeFetchError, tradeError, getErrorContent],
  );
  const renderFooter = useCallback(
    () => (
      <div
        className={`w-full flex-shrink-0 flex flex-row items-center
      ${spacingConfig.gapBetweenCancelAndConfirmButtons}
      ${borderClassToVisualizeElementsAndSpacing}`}
      >
        <Button
          onClick={() => navigateBack(SCREENS.INITIAL)}
          fullWidth
          disabled={isProcessing}
        >
          {TRADE_CARD.SCREENS.TRADE_CONFIRMATION_REQUEST.LABELS.CANCEL_BUTTON}
        </Button>
        <Button
          onClick={handleConfirm}
          fullWidth
          loading={isProcessing || isFetchingFees}
          disabled={isProcessing || isFetchingFees || !fees}
        >
          {TRADE_CARD.SCREENS.TRADE_CONFIRMATION_REQUEST.LABELS.CONFIRM_BUTTON}
        </Button>
      </div>
    ),
    [isProcessing, isFetchingFees, fees, handleConfirm],
  );
  return (
    <div
      className={`flex flex-col flex-grow w-full h-full
      ${spacingConfig.rootGap}
      ${borderClassToVisualizeElementsAndSpacing}`}
    >
      {renderBody()}
      {showFeeEstimationLoadingAlert && (
        <LoadingAlert
          title={TRADE.PROCESSING.TRADE_FEE_ESTIMATION_TAKING_LONGER_TITLE}
          description={
            TRADE.PROCESSING.TRADE_FEE_ESTIMATION_TAKING_LONGER_DESCRIPTION
          }
        />
      )}
      {showTradeLoadingAlert && (
        <LoadingAlert
          title={TRADE.PROCESSING.TRADE_IN_PROGRESS_TAKING_LONGER_TITLE}
          description={
            TRADE.PROCESSING.TRADE_IN_PROGRESS_TAKING_LONGER_DESCRIPTION
          }
        />
      )}
      {feeFetchError && getErrorContent(feeFetchError) && (
        <ErrorAlert {...getErrorContent(feeFetchError)} />
      )}
      {tradeError?.errorCode && <ErrorAlert {...getErrorContent(tradeError)} />}
      {renderFooter()}
    </div>
  );
};
export default React.memo(TradeConfirmationRequest);
